import { Alert, Box, CircularProgress, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';

import { toast } from '@/components/core/toaster';
import { useDebouncedBlockContent } from '@/hooks/use-debounced-block-content';
import { useIsUnitAdmin } from '@/hooks/use-is-unit-admin';
import { useOrgProfile } from '@/hooks/use-org-profile';
import { useUnitDetails, type UnitDetailsIdentityUpdate } from '@/hooks/use-unit-details';
import type { UnitIdentityBlockContent } from '@/types/database';

export function UnitIdentityBlockEditPanel({
  content,
  onUpdate,
}: {
  content: UnitIdentityBlockContent;
  onUpdate: (content: UnitIdentityBlockContent) => void;
}) {
  const [draft, update] = useDebouncedBlockContent(content, onUpdate);
  const { unitDetails, loading, save } = useUnitDetails();
  const { profile } = useOrgProfile();
  // unit_details is admin-only to write (its RLS keys off the same admin role
  // as every other unit_* table). Non-admins still see the values, just can't
  // edit them here — the same record is editable from the hub's Details page.
  const { isAdmin } = useIsUnitAdmin();

  const handleSave = (updates: UnitDetailsIdentityUpdate) => {
    void save(updates, profile?.unit_type ?? null).catch((error) => {
      toast.error(error instanceof Error ? error.message : 'Could not save unit details.');
    });
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Unit name, type, and number come from your unit record and stay in sync with Scouting Ops.
        {isAdmin
          ? ' Council, district, and chartered organization can be edited here or in the hub — either way they live in one place.'
          : ' Council, district, and chartered organization are managed by a unit admin in Scouting Ops.'}
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} aria-label="Loading unit details" />
        </Box>
      ) : (
        <Stack spacing={2}>
          <TextField
            label="Chartered organization"
            defaultValue={unitDetails?.chartered_org_name ?? ''}
            onBlur={(event) => handleSave({ chartered_org_name: event.target.value || null })}
            disabled={!isAdmin}
            fullWidth
          />
          <TextField
            label="Council"
            defaultValue={unitDetails?.council_name ?? ''}
            onBlur={(event) => handleSave({ council_name: event.target.value || null })}
            disabled={!isAdmin}
            fullWidth
          />
          <TextField
            label="District"
            defaultValue={unitDetails?.district_name ?? ''}
            onBlur={(event) => handleSave({ district_name: event.target.value || null })}
            disabled={!isAdmin}
            fullWidth
          />
        </Stack>
      )}

      <Divider />

      <FormControlLabel
        control={
          <Switch
            checked={draft.showMeetingSchedule !== false}
            onChange={(event) => update({ ...draft, showMeetingSchedule: event.target.checked }, { immediate: true })}
          />
        }
        label="Show meeting schedule"
      />
      <Typography variant="caption" color="text.secondary">
        Meeting day, time, and location are managed in Scouting Ops and shown here automatically.
      </Typography>
    </Stack>
  );
}
